import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { add as addToCart } from '../../Services/Cart.service';
import {
  receive as receiveCart,
  triggerAddToCartAnimation,
} from '../../Actions/Cart.action';
import { translate } from '../../Services/translation';
import QuantityInput from '../QuantityInput/QuantityInput';
import LoadingSpinner from '../LoadingSpinner';
import { useCallback } from 'react';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import { setBuyButtonLoading } from '../../Actions/BuyButton.action';
import clsx from 'clsx';

export interface BuyButtonAttributes {
  articleNumber?: string;
  disabled?: boolean;
  showQuantityInput?: boolean;
  onClick?: Function;
  className?: string;
  label?: any;
  quantity?: number;
  href?: string;
  packagingSize: number;
  btnClassName?: string;
}

const BuyButton = (props: BuyButtonAttributes) => {
  const {
    articleNumber: localArticleNumber,
    showQuantityInput = true,
    className = '',
    onClick: customOnClick,
    label,
    quantity: initialQuantity,
    href,
    disabled: disabledProp,
    packagingSize,
    btnClassName,
  } = props;
  const {
    articleNumber: reducerArticleNumber,
    disabled = false,
    isLoading = false,
  } = useTypedSelector((state) => state.buyButton);

  const dispatch = useDispatch();
  const [quantity, setQuantity] = useState<number | undefined>(
    initialQuantity ?? 1
  );

  const [showLoadingSpinner, setShowLoadingSpinner] =
    useState<boolean>(false);

  const articleNumber = useMemo(
    () => localArticleNumber ?? reducerArticleNumber,
    [localArticleNumber, reducerArticleNumber]
  );

  const setIsLoading = (isLoading: boolean) => {
    dispatch(setBuyButtonLoading(isLoading));
  };

  const onClick = async () => {
    if (customOnClick) {
      customOnClick(articleNumber, quantity);
    } else if (!href) {
      setIsLoading(true);
      const loadingSpinnerTimeout = setTimeout(
        () => setShowLoadingSpinner(true),
        150
      );

      try {
        const cart = await addToCart({ articleNumber, quantity });
        dispatch(receiveCart(cart));
        dispatch(triggerAddToCartAnimation());
      } catch (err) {
        console.log(err);
      } finally {
        clearTimeout(loadingSpinnerTimeout);
        setShowLoadingSpinner(false);
        setIsLoading(false);
      }
    }
  };

  const _label = useMemo(
    () =>
      label ??
      (href ? translate('general.show') : translate('general.add')),
    [href, label]
  );

  return (
    <div
      className={clsx(`${className ?? ''} buy-button`, 'flex', 'gap-2')}
    >
      {showQuantityInput && (
        <QuantityInput
          packagingSize={packagingSize}
          quantity={quantity}
          showButtons={true}
          onChange={(value: number) => setQuantity(value)}
          className={clsx('flex-1')}
        />
      )}
      {href && (
        <a
          onClick={onClick}
          className={clsx(
            'btn',
            'text-md',
            '!flex',
            'items-center',
            'justify-center',
            'btn--primary',
            'self-stretch',
            'relative',
            'min-w-fit',
            'px-2.5'
          )}
          href={!disabled ? href : undefined}
        >
          {showLoadingSpinner ? (
            <LoadingSpinner className="h-6 w-6 animate-spin" />
          ) : (
            _label
          )}
        </a>
      )}
      {!href && (
        <button
          onClick={onClick}
          className={clsx(
            'btn',
            'text-md',
            'flex',
            'items-center',
            'justify-center',
            'btn--primary',
            'self-stretch',
            'relative',
            'min-w-fit',
            'px-2.5',
            btnClassName
          )}
          disabled={
            disabled ||
            disabledProp ||
            isLoading ||
            showLoadingSpinner ||
            !articleNumber ||
            (showQuantityInput && (!quantity || quantity <= 0))
          }
        >
          {showLoadingSpinner ? (
            <LoadingSpinner className="h-6 w-6 animate-spin" />
          ) : (
            _label
          )}
        </button>
      )}
    </div>
  );
};

export default BuyButton;
